// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Card, CardHeader, Box, Stack, TextField, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import Iconify from '../iconify';
import { useChart } from '../chart';
import { UsageTrend } from './types';

interface UsageTrendsChartProps {
  title: string;
  subheader?: string;
  data: UsageTrend[];
  onDateChange?: (startDate: string, endDate: string) => void;
}

interface GroupedPoint {
  label: string;
  tokens: number;
  cost: number;
  queries: number;
}

type GroupingMode = 'daily' | 'weekly' | 'monthly';

// ─── grouping helpers ────────────────────────────────────────────────────────

/**
 * Decide grouping based on the span of the visible date range:
 *   ≤ 7 days   → daily   (legend per day)
 *   8 – 62 days → weekly  (legend per week, covers 1 month & 2-month cases)
 *   > 62 days  → monthly (legend per month)
 */
function getGroupingMode(data: UsageTrend[]): GroupingMode {
  if (data.length < 2) return 'daily';
  const sorted = data.map((d) => new Date(d.date + 'T00:00:00')).sort((a, b) => a.getTime() - b.getTime());
  const rangeDays = (sorted[sorted.length - 1].getTime() - sorted[0].getTime()) / 86400000;
  if (rangeDays <= 7) return 'daily';
  if (rangeDays <= 62) return 'weekly';
  return 'monthly';
}

/** Returns the Monday of the ISO week containing `date`. */
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sun
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

function groupDataByMode(data: UsageTrend[], mode: GroupingMode): GroupedPoint[] {
  if (mode === 'daily') {
    return data.map((d) => ({
      label: new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      tokens: d.tokens,
      cost: d.cost,
      queries: d.queries,
    }));
  }

  // Ordered map to preserve chronological insertion order
  const buckets = new Map<string, GroupedPoint>();

  for (const item of data) {
    const date = new Date(item.date + 'T00:00:00');
    let key: string;
    let label: string;

    if (mode === 'weekly') {
      const ws = getWeekStart(date);
      key = ws.toISOString().split('T')[0];
      const we = new Date(ws);
      we.setDate(we.getDate() + 6);
      const s = ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const e = we.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      label = `${s}–${e}`;
    } else {
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    }

    if (!buckets.has(key)) buckets.set(key, { label, tokens: 0, cost: 0, queries: 0 });
    const b = buckets.get(key)!;
    b.tokens += item.tokens;
    b.cost = parseFloat((b.cost + item.cost).toFixed(2));
    b.queries += item.queries;
  }

  return Array.from(buckets.values());
}

const MODE_LABEL: Record<GroupingMode, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

// ─── component ───────────────────────────────────────────────────────────────

export default function UsageTrendsChart({ title, subheader, data, onDateChange }: UsageTrendsChartProps) {
  const theme = useTheme();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Normalize data to always be an array
  const normalizedData = useMemo(() => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray((data as any).usageTrends)) return (data as any).usageTrends;
    return [];
  }, [data]);

  // Initialize date pickers from sorted min/max — fires only once when data first arrives
  const datesInitialized = React.useRef(false);
  useEffect(() => {
    if (!datesInitialized.current && normalizedData.length > 0) {
      const sorted = normalizedData.map((d) => d.date).sort();
      setStartDate(sorted[0]);
      setEndDate(sorted[sorted.length - 1]);
      datesInitialized.current = true;
    }
  }, [normalizedData]);

  // Client-side date filtering
  const filteredData = useMemo(() => {
    if (!startDate && !endDate) return normalizedData;
    return normalizedData.filter((d) => {
      if (startDate && d.date < startDate) return false;
      if (endDate && d.date > endDate) return false;
      return true;
    });
  }, [normalizedData, startDate, endDate]);

  // Auto-select grouping mode based on visible date range
  const groupingMode = useMemo(() => getGroupingMode(filteredData), [filteredData]);
  const groupedData = useMemo(() => groupDataByMode(filteredData, groupingMode), [filteredData, groupingMode]);

  // Totals derived from the currently filtered date range — update when dates change
  const totalTokens = useMemo(() => filteredData.reduce((sum, d) => sum + (d.tokens || 0), 0), [filteredData]);
  const totalCost = useMemo(
    () => parseFloat(filteredData.reduce((sum, d) => sum + (d.cost || 0), 0).toFixed(2)),
    [filteredData]
  );

  const isSinglePoint = groupedData.length === 1;

  // Mixed chart: Bar for Tokens (left y-axis), Line for Cost (right y-axis)
  const chartSeries = [
    { name: 'Tokens', data: groupedData.map((d) => d.tokens), type: 'bar' },
    { name: 'Cost ($)', data: groupedData.map((d) => d.cost), type: 'line' },
  ];

  const chartOptions = useChart({
    xaxis: {
      type: 'category',
      categories: groupedData.map((d) => d.label),
      labels: {
        rotate: groupedData.length > 7 ? -35 : 0,
        hideOverlappingLabels: true,
        trim: true,
      },
    },
    yaxis: [
      {
        title: { text: 'Tokens' },
        labels: {
          formatter: (val: number) => {
            if (val == null) return '';
            if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
            if (val >= 1_000) return `${(val / 1_000).toFixed(0)}K`;
            return String(val);
          },
        },
      },
      {
        opposite: true,
        title: { text: 'Cost ($)' },
        labels: { formatter: (val: number) => (val == null ? '' : `$${val.toFixed(2)}`) },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        {
          formatter: (val: number) => {
            if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(2)}M tokens`;
            if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K tokens`;
            return `${val?.toLocaleString()} tokens`;
          },
        },
        { formatter: (val: number) => `$${val?.toFixed(2)}` },
      ],
    },
    // stroke width: 0 for bars, 3 for the line overlay
    stroke: { width: [0, isSinglePoint ? 0 : 3], curve: 'smooth' },
    fill: { type: ['solid', 'solid'], opacity: [0.85, 1] },
    plotOptions: {
      bar: {
        // Widen the bar when there is only one column so it is clearly visible
        columnWidth: isSinglePoint ? '15%' : groupedData.length <= 4 ? '30%' : groupedData.length <= 8 ? '50%' : '70%',
        borderRadius: 4,
      },
    },
    // For a single point the line has nothing to draw — show a large dot instead
    markers: { size: [0, isSinglePoint ? 8 : 5] },
  });

  const formatTokens = (val: number) => {
    if (!val && val !== 0) return '—';
    if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(2)}M`;
    if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
    return val.toLocaleString();
  };

  return (
    <Card>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ pr: 2.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <CardHeader
            title={`${title} (${MODE_LABEL[groupingMode]})`}
            subheader={
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                {subheader && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {subheader}
                  </Typography>
                )}
                {(totalTokens !== undefined || totalCost !== undefined) && (
                  <Stack direction="row" spacing={1}>
                    {totalTokens !== undefined && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.secondary.main, 0.12),
                          color: theme.palette.secondary.dark,
                        }}
                      >
                        <Iconify icon="mdi:counter" width={14} />
                        <Typography variant="caption" fontWeight={700}>
                          {formatTokens(totalTokens)} tokens
                        </Typography>
                      </Box>
                    )}
                    {totalCost !== undefined && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.25,
                          py: 0.4,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.error.main, 0.12),
                          color: theme.palette.error.dark,
                        }}
                      >
                        <Iconify icon="mdi:currency-usd" width={14} />
                        <Typography variant="caption" fontWeight={700}>
                          ${typeof totalCost === 'number' ? totalCost.toFixed(2) : totalCost}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>
            }
          />
        </Stack>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            px: { xs: 2.5, sm: 0 },
            pb: { xs: 1.5, sm: 0 },
            gap: 1,
          }}
        >
          <Iconify icon="mdi:calendar-range" width={20} sx={{ color: 'text.secondary' }} />
          <TextField
            label="From"
            type="date"
            size="small"
            value={startDate || ''}
            onChange={(e) => {
              const val = e.target.value;
              setStartDate(val);
              if (val && endDate) onDateChange?.(val, endDate);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 155 } }}
          />
          <Typography variant="body2" sx={{ color: 'text.disabled', display: { xs: 'none', sm: 'block' } }}>
            —
          </Typography>
          <TextField
            label="To"
            type="date"
            size="small"
            value={endDate || ''}
            onChange={(e) => {
              const val = e.target.value;
              setEndDate(val);
              if (startDate && val) onDateChange?.(startDate, val);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 155 } }}
          />
        </Stack>
      </Stack>
      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        <ReactApexChart type="bar" series={chartSeries} options={chartOptions} height={364} />
      </Box>
    </Card>
  );
}
